import { useQuery } from 'react-query';
import type { UseQueryOptions } from 'react-query';

import type {
  DemandSubAccountQuery,
  DemandSubAccountWithAdditionalAttributes,
  PortalSubAccount,
  SubAccountsQuery,
} from '@portal/api-models';

import { client } from '../client';
import { QueryKey } from '../types';

export const getSubAccounts = async (params?: SubAccountsQuery) =>
  client
    .get<PortalSubAccount[]>('/finance/subaccounts', { params })
    .then(({ data }) => data);

export const getSubAccountByUuid = async (uuid: string) =>
  client
    .get<PortalSubAccount>(`/finance/subaccounts/${uuid}`)
    .then(({ data }) => data);

export const getDemandSubAccounts = async (params?: DemandSubAccountQuery) =>
  client
    .get<DemandSubAccountWithAdditionalAttributes[]>(
      '/finance/demand-sub-accounts',
      { params }
    )
    .then(({ data }) => data);

/**
 * Replaces usage of Redux Saga and selectors.
 *
 * Examples of selectors to be refactored:
 * - selectDemandSubAccounts
 * - selectSupplySubAccounts
 *
 */
export const useGetSubAccountsQuery = (
  params?: SubAccountsQuery,
  options?: Omit<UseQueryOptions<PortalSubAccount[]>, 'queryKey' | 'queryFn'>
) =>
  useQuery(
    [QueryKey.SubAccounts, params],
    async () => getSubAccounts(params),
    options
  );

export const useGetSubAccountByUuidQuery = (
  uuid: string,
  options?: Omit<UseQueryOptions<PortalSubAccount>, 'queryKey' | 'queryFn'>
) =>
  useQuery(
    [QueryKey.SubAccounts, uuid],
    async () => getSubAccountByUuid(uuid),
    options
  );

export const useGetSubAccountsByIdQuery = (
  params?: SubAccountsQuery,
  options?: Omit<
    UseQueryOptions<Record<number, PortalSubAccount>>,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery(
    [QueryKey.SubAccountsById, params],
    async () => {
      const data = await getSubAccounts(params);
      return data.reduce((acc, item) => {
        acc[item.id] = item;
        return acc;
      }, {} as Record<number, PortalSubAccount>);
    },
    options
  );
};
